import { useState, useRef, useCallback } from 'react';
import { Product } from '@/mocks/products';

interface ProductCarouselSectionProps {
  categoryTitle: string;
  products: Product[];
}

export default function ProductCarouselSection({ categoryTitle, products }: ProductCarouselSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const scrollAmount = scrollRef.current.clientWidth * 0.75;
    scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl md:text-3xl text-foreground-950">
            {categoryTitle}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-700 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          onScroll={checkScroll}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {products.map((product) => (
            <a
              key={product.id}
              href={product.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex-shrink-0 w-[160px] sm:w-[200px] md:w-[240px] snap-start bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:shadow-md hover:border-primary-300/50 transition-all"
            >
              <div className="aspect-square overflow-hidden bg-background-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-3">
                <h3 className="text-xs md:text-sm font-medium text-foreground-800 line-clamp-2 leading-snug mb-1 group-hover:text-primary-600 transition-colors">
                  {product.name}
                </h3>
                <p className="text-xs md:text-sm font-bold text-primary-600">
                  {product.price}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
}